import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../services/firebase';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';

const AdminAnalytics = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [activeTab, setActiveTab] = useState('OVERVIEW');
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  
 
  const [analyticsData, setAnalyticsData] = useState({
    overview: {
      totalRevenue: 0,
      totalAppointments: 0,
      totalEstimates: 0,
      totalClients: 0,
      avgResponseTime: 0,
      completionRate: 0
    },
    appointments: {
      byStatus: {},
      byMonth: {},
      byService: {},
      recentTrends: []
    },
    estimates: {
      byStatus: {},
      avgValue: 0,
      conversionRate: 0,
      monthlyTrends: []
    },
    clients: {
      newClients: [],
      activeClients: 0,
      clientRetention: 0,
      topClients: []
    },
    revenue: {
      monthlyRevenue: {},
      serviceRevenue: {},
      projectedRevenue: 0,
      growthRate: 0
    },
    performance: {
      responseTime: {},
      completionTime: {},
      customerSatisfaction: 0,
      efficiency: {}
    }
  });

  const [dateRange, setDateRange] = useState('30'); // days
  const [selectedMetric, setSelectedMetric] = useState('revenue');

  useEffect(() => {
    if (currentUser) {
      loadAnalyticsData();
    }
  }, [currentUser, dateRange]);

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(dateRange));

      // Load appointments data
      const appointmentsRef = collection(db, 'appointments');
      const appointmentsSnapshot = await getDocs(appointmentsRef);
      const appointments = [];
      appointmentsSnapshot.forEach((doc) => {
        const data = doc.data();
        appointments.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
          appointmentDate: data.appointmentDate?.toDate ? data.appointmentDate.toDate() : new Date(data.appointmentDate)
        });
      });

      // Load estimates data
      const estimatesRef = collection(db, 'estimates');
      const estimatesSnapshot = await getDocs(estimatesRef);
      const estimates = [];
      estimatesSnapshot.forEach((doc) => {
        const data = doc.data();
        estimates.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt)
        });
      });

      // Load users data
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const users = [];
      usersSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.role !== 'admin') {
          users.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt)
          });
        }
      });

      // Load support tickets data
      let tickets = [];
      try {
        const ticketsRef = collection(db, 'tickets');
        const ticketsSnapshot = await getDocs(ticketsRef);
        ticketsSnapshot.forEach((doc) => {
          const data = doc.data();
          tickets.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt)
          });
        });
      } catch (error) {
        console.warn('Tickets collection not found, skipping ticket analytics');
      }

      // Process analytics data
      const processedData = processAnalyticsData(appointments, estimates, users, tickets, startDate, endDate);
      setAnalyticsData(processedData);

    } catch (error) {
      console.error('Error loading analytics data:', error);
      showNotification('Failed to load analytics data. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const processAnalyticsData = (appointments, estimates, users, tickets, startDate, endDate) => {
    // Filter data by date range
    const filteredAppointments = appointments.filter(apt => apt.createdAt >= startDate && apt.createdAt <= endDate);
    const filteredEstimates = estimates.filter(est => est.createdAt >= startDate && est.createdAt <= endDate);
    const filteredUsers = users.filter(user => user.createdAt >= startDate && user.createdAt <= endDate);
    const filteredTickets = tickets.filter(ticket => ticket.createdAt >= startDate && ticket.createdAt <= endDate);

    // Calculate overview metrics
    const totalRevenue = filteredAppointments
      .filter(apt => apt.status === 'completed' && apt.cost)
      .reduce((sum, apt) => sum + parseFloat(apt.cost || 0), 0);

    const completedAppointments = filteredAppointments.filter(apt => apt.status === 'completed').length;
    const totalAppointments = filteredAppointments.length;
    const completionRate = totalAppointments > 0 ? (completedAppointments / totalAppointments) * 100 : 0;

    // Appointment analytics
    const appointmentsByStatus = filteredAppointments.reduce((acc, apt) => {
      acc[apt.status] = (acc[apt.status] || 0) + 1;
      return acc;
    }, {});

    const appointmentsByService = filteredAppointments.reduce((acc, apt) => {
      const service = apt.service || 'Other';
      acc[service] = (acc[service] || 0) + 1;
      return acc;
    }, {});

    // Estimate analytics
    const estimatesByStatus = filteredEstimates.reduce((acc, est) => {
      acc[est.status] = (acc[est.status] || 0) + 1;
      return acc;
    }, {});

    const avgEstimateValue = filteredEstimates.length > 0 
      ? filteredEstimates
          .filter(est => est.quoteAmount)
          .reduce((sum, est) => sum + parseFloat(est.quoteAmount || 0), 0) / filteredEstimates.length
      : 0;

    const acceptedEstimates = filteredEstimates.filter(est => est.status === 'accepted').length;
    const conversionRate = filteredEstimates.length > 0 ? (acceptedEstimates / filteredEstimates.length) * 100 : 0;

    // Monthly trends
    const monthlyData = {};
    const monthlyRevenue = {};
    
    filteredAppointments.forEach(apt => {
      const month = apt.createdAt.toISOString().substring(0, 7); // YYYY-MM
      monthlyData[month] = (monthlyData[month] || 0) + 1;
      
      if (apt.status === 'completed' && apt.cost) {
        monthlyRevenue[month] = (monthlyRevenue[month] || 0) + parseFloat(apt.cost);
      }
    });

    // Client analytics
    const activeClients = users.filter(user => {
      // Consider active if they have appointments or estimates in the last 30 days
      const hasRecentActivity = appointments.some(apt => 
        apt.userId === user.id && 
        apt.createdAt >= new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      ) || estimates.some(est => 
        est.userId === user.id && 
        est.createdAt >= new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      );
      return hasRecentActivity;
    }).length;

    // Top clients by revenue
    const clientRevenue = {};
    appointments
      .filter(apt => apt.status === 'completed' && apt.cost)
      .forEach(apt => {
        clientRevenue[apt.userId] = (clientRevenue[apt.userId] || 0) + parseFloat(apt.cost);
      });

    const topClients = Object.entries(clientRevenue)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([userId, revenue]) => {
        const user = users.find(u => u.id === userId);
        return {
          userId,
          name: user ? `${user.firstName} ${user.lastName}` : 'Unknown Client',
          email: user ? user.email : 'N/A',
          revenue
        };
      });

    // Performance metrics
    const avgResponseTime = filteredTickets.length > 0 
      ? filteredTickets
          .filter(ticket => ticket.messages && ticket.messages.length > 1)
          .reduce((sum, ticket) => {
            const firstMessage = ticket.messages[0];
            const firstResponse = ticket.messages.find(msg => msg.isAdmin);
            if (firstResponse && firstMessage) {
              const responseTime = new Date(firstResponse.timestamp) - new Date(firstMessage.timestamp);
              return sum + (responseTime / (1000 * 60 * 60)); // hours
            }
            return sum;
          }, 0) / filteredTickets.length
      : 0;

    return {
      overview: {
        totalRevenue,
        totalAppointments: filteredAppointments.length,
        totalEstimates: filteredEstimates.length,
        totalClients: filteredUsers.length,
        avgResponseTime,
        completionRate
      },
      appointments: {
        byStatus: appointmentsByStatus,
        byMonth: monthlyData,
        byService: appointmentsByService,
        recentTrends: Object.entries(monthlyData).sort()
      },
      estimates: {
        byStatus: estimatesByStatus,
        avgValue: avgEstimateValue,
        conversionRate,
        monthlyTrends: Object.entries(monthlyData).sort()
      },
      clients: {
        newClients: filteredUsers,
        activeClients,
        clientRetention: users.length > 0 ? (activeClients / users.length) * 100 : 0,
        topClients
      },
      revenue: {
        monthlyRevenue,
        serviceRevenue: appointmentsByService,
        projectedRevenue: totalRevenue * (365 / parseInt(dateRange)),
        growthRate: calculateGrowthRate(monthlyRevenue)
      },
      performance: {
        responseTime: { avg: avgResponseTime },
        completionTime: {},
        customerSatisfaction: 85, // Placeholder
        efficiency: { completionRate }
      }
    };
  };

  const calculateGrowthRate = (monthlyRevenue) => {
    const months = Object.keys(monthlyRevenue).sort();
    if (months.length < 2) return 0;
    
    const currentMonth = monthlyRevenue[months[months.length - 1]] || 0;
    const previousMonth = monthlyRevenue[months[months.length - 2]] || 0;
    
    return previousMonth > 0 ? ((currentMonth - previousMonth) / previousMonth) * 100 : 0;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(1)}%`;
  };

  const MetricCard = ({ title, value, subtitle, icon, color = '#2e7d32', trend }) => (
    <div style={{
      background: windowWidth <= 768 ? '#f8f9fa' : 'white',
      borderRadius: '8px',
      padding: windowWidth <= 480 ? '1rem 0.5rem' : windowWidth <= 768 ? '1rem' : '1.5rem',
      boxShadow: windowWidth <= 768 ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.1)',
      border: windowWidth <= 768 ? 'none' : '1px solid #e5e7eb',
      width: windowWidth <= 480 ? '90%' : '100%',
      maxWidth: windowWidth <= 480 ? '200px' : '100%',
      minWidth: 0,
      overflow: 'visible',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      margin: windowWidth <= 480 ? '0 auto' : '0'
    }}>
      {/* Icon */}
      <div style={{
        background: `${color}20`,
        borderRadius: '6px',
        padding: windowWidth <= 480 ? '0.5rem' : '0.75rem',
        fontSize: windowWidth <= 480 ? '1.2rem' : '1.5rem',
        marginBottom: '0.75rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: color
      }}>
        {icon}
      </div>
      
      {/* Value */}
      <div style={{ 
        fontSize: windowWidth <= 480 ? '1.5rem' : windowWidth <= 768 ? '1.75rem' : '2rem', 
        fontWeight: '700', 
        color: color,
        marginBottom: '0.5rem',
        lineHeight: 1.1
      }}>
        {value}
      </div>
      
      {/* Title */}
      <div style={{ 
        fontSize: windowWidth <= 480 ? '0.7rem' : '0.875rem', 
        color: 'var(--gray-600)',
        fontWeight: '500',
        lineHeight: windowWidth <= 480 ? 1.1 : 1.2,
        textAlign: 'center',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        hyphens: 'auto',
        whiteSpace: 'normal',
        width: '100%',
        maxWidth: '100%'
      }}>
        {title}
      </div>
      
      {/* Subtitle */}
      {subtitle && (
        <div style={{ 
          fontSize: windowWidth <= 480 ? '0.6rem' : '0.75rem', 
          color: '#9ca3af',
          marginTop: '0.25rem',
          textAlign: 'center',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          hyphens: 'auto',
          whiteSpace: 'normal',
          width: '100%',
          maxWidth: '100%'
        }}>
          {subtitle}
        </div>
      )}
      
      {/* Trend */}
      {trend && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.25rem',
          fontSize: windowWidth <= 480 ? '0.6rem' : '0.75rem',
          color: trend > 0 ? '#10b981' : trend < 0 ? '#ef4444' : '#6b7280',
          marginTop: '0.5rem'
        }}>
          <span>{trend > 0 ? '↗️' : trend < 0 ? '↘️' : '➡️'}</span>
          <span>{Math.abs(trend).toFixed(1)}% vs last period</span>
        </div>
      )}
    </div>
  );

  const ChartCard = ({ title, children }) => (
    <div style={{
      background: 'white',
      borderRadius: '8px',
      padding: windowWidth < 480 ? '0.75rem' : windowWidth < 768 ? '1rem' : '1.25rem',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e5e7eb',
      maxWidth: '100%',
      overflow: 'hidden',
      boxSizing: 'border-box'
    }}>
      <h3 style={{ 
        margin: '0 0 1rem 0', 
        fontSize: windowWidth < 480 ? '0.9rem' : windowWidth < 768 ? '1rem' : '1.1rem', 
        fontWeight: '600', 
        color: '#1f2937',
        lineHeight: 1.2
      }}>
        {title}
      </h3>
      {children}
    </div>
  );

  return (
    <Layout>
      <div className="analytics-container admin-analytics-page" style={{ 
        padding: windowWidth < 768 ? '0.5rem' : '1rem 2rem',
        maxWidth: '100%',
        overflowX: 'hidden',
        overflowY: 'visible',
        boxSizing: 'border-box',
        height: 'auto',
        minHeight: 'auto'
      }}>
        {/* Toast Notification */}
        {notification && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: windowWidth < 768 ? '0.5rem' : '20px',
            left: windowWidth < 768 ? '0.5rem' : 'auto',
            zIndex: 1000,
            maxWidth: windowWidth < 768 ? 'none' : '400px',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            backgroundColor: notification.type === 'success' ? '#4caf50' : '#f44336',
            color: 'white',
            fontSize: '0.875rem',
            fontWeight: '500',
            wordWrap: 'break-word'
          }}>
            {notification.message}
          </div>
        )}

        {/* Header */}
        <div style={{ 
          marginBottom: windowWidth < 768 ? '1rem' : '2rem',
          maxWidth: '100%',
          overflowX: 'hidden'
        }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: windowWidth < 768 ? 'column' : 'row',
            justifyContent: 'space-between', 
            alignItems: windowWidth < 768 ? 'flex-start' : 'center',
            gap: windowWidth < 768 ? '1rem' : '0',
            maxWidth: '100%'
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{ 
                fontSize: windowWidth < 480 ? '1.25rem' : windowWidth < 768 ? '1.5rem' : '2rem', 
                fontWeight: 'bold', 
                color: '#2e7d32', 
                margin: 0,
                wordBreak: 'break-word'
              }}>
                ⊞ Analytics Dashboard
              </h1>
              <p style={{ 
                color: '#666', 
                marginTop: '0.5rem',
                fontSize: windowWidth < 768 ? '0.8rem' : '1rem',
                margin: '0.5rem 0 0 0'
              }}>
                Comprehensive business insights and performance metrics
              </p>
            </div>
            
            <div style={{ 
              display: 'flex', 
              flexDirection: windowWidth < 480 ? 'column' : 'row',
              gap: windowWidth < 480 ? '0.5rem' : '1rem', 
              alignItems: windowWidth < 480 ? 'stretch' : 'center',
              flexShrink: 0,
              width: windowWidth < 768 ? '100%' : 'auto'
            }}>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                style={{
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  backgroundColor: 'white',
                  width: windowWidth < 480 ? '100%' : 'auto',
                  minWidth: '120px',
                  maxWidth: '100%'
                }}
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="365">Last year</option>
              </select>
              
              <button
                className="btn btn-primary"
                onClick={loadAnalyticsData}
                disabled={loading}
                style={{
                  padding: '0.5rem 0.75rem',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  opacity: loading ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  whiteSpace: 'nowrap',
                  width: windowWidth < 480 ? '100%' : 'auto',
                  minWidth: 'fit-content'
                }}
              >
                    <img 
                      src="/refresh.jpg" 
                  alt={loading ? "Loading" : "Refresh"} 
                      style={{ 
                    width: '16px', 
                    height: '16px',
                        objectFit: 'contain'
                      }} 
                    />
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⊞</div>
            <p>Loading analytics data...</p>
          </div>
        ) : (
          <>
            {/* Overview Metrics - HARDCODED ALL 6 CARDS */}
            <div className="stats-cards-container" style={{ 
              display: windowWidth <= 480 ? 'flex' : 'grid',
              flexDirection: windowWidth <= 480 ? 'column' : 'row',
              gridTemplateColumns: windowWidth <= 480 ? '1fr' : 
                                   windowWidth <= 768 ? 'repeat(2, 1fr)' : 
                                   windowWidth <= 1024 ? 'repeat(3, 1fr)' : 
                                   'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '0.75rem',
              marginBottom: '2rem',
              padding: '1rem',
              maxWidth: '100%',
              overflowX: 'hidden',
              overflowY: 'visible',
              boxSizing: 'border-box',
              minHeight: windowWidth <= 480 ? '500px' : 'auto',
              height: 'auto',
              justifyItems: 'center',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {/* CARD 1: Total Revenue */}
              <div className="metric-card-modern" style={{ 
                background: '#f8f9fa', 
                border: '1px solid #e9ecef',
                padding: '1rem',
                width: '100%',
                minHeight: '60px',
                display: 'flex',
                flexDirection: windowWidth <= 480 ? 'row' : 'column',
                alignItems: 'center',
                justifyContent: windowWidth <= 480 ? 'space-between' : 'center',
                borderRadius: '8px',
                marginBottom: '0.75rem',
                visibility: 'visible',
                opacity: 1,
                position: 'relative',
                zIndex: 1
              }}>
                <div style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>
                  Total Revenue
                </div>
                <div style={{ color: '#10b981', fontSize: '1.5rem', fontWeight: '700' }}>
                  {formatCurrency(analyticsData.overview.totalRevenue)}
                </div>
              </div>

              {/* CARD 2: Total Appointments */}
              <div className="metric-card-modern" style={{ 
                background: '#f8f9fa', 
                border: '1px solid #e9ecef',
                padding: '1rem',
                width: '100%',
                minHeight: '60px',
                display: 'flex',
                flexDirection: windowWidth <= 480 ? 'row' : 'column',
                alignItems: 'center',
                justifyContent: windowWidth <= 480 ? 'space-between' : 'center',
                borderRadius: '8px',
                marginBottom: '0.75rem',
                visibility: 'visible',
                opacity: 1,
                position: 'relative',
                zIndex: 1
              }}>
                <div style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>
                  Total Appointments
                </div>
                <div style={{ color: '#3b82f6', fontSize: '1.5rem', fontWeight: '700' }}>
                  {analyticsData.overview.totalAppointments || 0}
                </div>
              </div>

              {/* CARD 3: Total Estimates */}
              <div className="metric-card-modern" style={{ 
                background: '#f8f9fa', 
                border: '1px solid #e9ecef',
                padding: '1rem',
                width: '100%',
                minHeight: '60px',
                display: 'flex',
                flexDirection: windowWidth <= 480 ? 'row' : 'column',
                alignItems: 'center',
                justifyContent: windowWidth <= 480 ? 'space-between' : 'center',
                borderRadius: '8px',
                marginBottom: '0.75rem',
                visibility: 'visible',
                opacity: 1,
                position: 'relative',
                zIndex: 1
              }}>
                <div style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>
                  Total Estimates
                </div>
                <div style={{ color: '#8b5cf6', fontSize: '1.5rem', fontWeight: '700' }}>
                  {analyticsData.overview.totalEstimates || 0}
                </div>
              </div>

              {/* CARD 4: New Clients */}
              <div className="metric-card-modern" style={{ 
                background: '#f8f9fa', 
                border: '1px solid #e9ecef',
                padding: '1rem',
                width: '100%',
                minHeight: '60px',
                display: 'flex',
                flexDirection: windowWidth <= 480 ? 'row' : 'column',
                alignItems: 'center',
                justifyContent: windowWidth <= 480 ? 'space-between' : 'center',
                borderRadius: '8px',
                marginBottom: '0.75rem',
                visibility: 'visible',
                opacity: 1,
                position: 'relative',
                zIndex: 1
              }}>
                <div style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>
                  New Clients
                </div>
                <div style={{ color: '#f59e0b', fontSize: '1.5rem', fontWeight: '700' }}>
                  {analyticsData.overview.totalClients || 0}
                </div>
              </div>

              {/* CARD 5: Completion Rate */}
              <div className="metric-card-modern" style={{ 
                background: '#f8f9fa', 
                border: '1px solid #e9ecef',
                padding: '1rem',
                width: '100%',
                minHeight: '60px',
                display: 'flex',
                flexDirection: windowWidth <= 480 ? 'row' : 'column',
                alignItems: 'center',
                justifyContent: windowWidth <= 480 ? 'space-between' : 'center',
                borderRadius: '8px',
                marginBottom: '0.75rem',
                visibility: 'visible',
                opacity: 1,
                position: 'relative',
                zIndex: 1
              }}>
                <div style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>
                  Completion Rate
                </div>
                <div style={{ color: '#10b981', fontSize: '1.5rem', fontWeight: '700' }}>
                  {formatPercentage(analyticsData.overview.completionRate)}
                </div>
              </div>

              {/* CARD 6: Avg Response Time */}
              <div className="metric-card-modern" style={{ 
                background: '#f8f9fa', 
                border: '1px solid #e9ecef',
                padding: '1rem',
                width: '100%',
                minHeight: '60px',
                display: 'flex',
                flexDirection: windowWidth <= 480 ? 'row' : 'column',
                alignItems: 'center',
                justifyContent: windowWidth <= 480 ? 'space-between' : 'center',
                borderRadius: '8px',
                marginBottom: '0.75rem',
                visibility: 'visible',
                opacity: 1,
                position: 'relative',
                zIndex: 1
              }}>
                <div style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>
                  Avg Response Time
                </div>
                <div style={{ color: '#ef4444', fontSize: '1.5rem', fontWeight: '700' }}>
                  {`${analyticsData.overview.avgResponseTime.toFixed(1)}h`}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
              marginBottom: windowWidth < 768 ? '1rem' : '2rem',
              maxWidth: '100%'
            }}>
              <div style={{ 
                display: 'flex', 
                borderBottom: '1px solid #eee',
                backgroundColor: '#f8f9fa',
                overflowX: windowWidth < 768 ? 'auto' : 'visible',
                scrollbarWidth: 'thin',
                WebkitOverflowScrolling: 'touch',
                maxWidth: '100%'
              }}>
                {[
                  { key: 'OVERVIEW', label: 'Overview', icon: '⊞' },
                  { key: 'APPOINTMENTS', label: 'Appointments', icon: '⊡' },
                  { key: 'ESTIMATES', label: 'Estimates', icon: '⊡' },
                  { key: 'CLIENTS', label: 'Clients', icon: '◯' },
                  { key: 'REVENUE', label: 'Revenue', icon: '◇' }
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    style={{
                      padding: windowWidth < 480 ? '0.5rem 0.75rem' : windowWidth < 768 ? '0.75rem 1rem' : '1rem 1.5rem',
                      border: 'none',
                      backgroundColor: activeTab === tab.key ? '#2e7d32' : 'transparent',
                      color: activeTab === tab.key ? 'white' : '#666',
                      cursor: 'pointer',
                      fontSize: windowWidth < 480 ? '0.7rem' : windowWidth < 768 ? '0.75rem' : '0.875rem',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: windowWidth < 480 ? '0.25rem' : '0.5rem',
                      borderBottom: activeTab === tab.key ? '2px solid #2e7d32' : '2px solid transparent',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      minWidth: 'fit-content',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <span style={{ fontSize: windowWidth < 480 ? '0.9rem' : windowWidth < 768 ? '1rem' : '1.1rem' }}>{tab.icon}</span>
                    {windowWidth <= 480 ? (tab.key === 'OVERVIEW' ? 'Over' : 
                                          tab.key === 'APPOINTMENTS' ? 'Appt' : 
                                          tab.key === 'ESTIMATES' ? 'Est' : 
                                          tab.key === 'CLIENTS' ? 'Cli' : 'Rev') : tab.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div style={{ 
                padding: windowWidth < 480 ? '0.75rem' : windowWidth < 768 ? '1rem' : '1.5rem',
                maxWidth: '100%',
                overflowX: 'hidden'
              }}>
                {/* Overview Tab */}
                {activeTab === 'OVERVIEW' && (
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: windowWidth < 768 ? '1fr' : '1fr 1fr', 
                    gap: windowWidth < 480 ? '1rem' : windowWidth < 768 ? '1.25rem' : '1.5rem',
                    maxWidth: '100%'
                  }}>
                    <ChartCard title="Appointment Status Distribution">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {Object.entries(analyticsData.appointments.byStatus).map(([status, count]) => (
                          <div key={status} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ textTransform: 'capitalize', fontSize: '0.875rem', color: '#374151' }}>
                              {status}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <div style={{
                                width: '100px',
                                height: '8px',
                                backgroundColor: '#e5e7eb',
                                borderRadius: '4px',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  width: `${(count / analyticsData.overview.totalAppointments) * 100}%`,
                                  height: '100%',
                                  backgroundColor: '#2e7d32'
                                }} />
                              </div>
                              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1f2937', minWidth: '30px' }}>
                                {count}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ChartCard>

                    <ChartCard title="Service Distribution">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {Object.entries(analyticsData.appointments.byService).slice(0, 5).map(([service, count]) => (
                          <div key={service} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.875rem', color: '#374151' }}>
                              {service}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <div style={{
                                width: '100px',
                                height: '8px',
                                backgroundColor: '#e5e7eb',
                                borderRadius: '4px',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  width: `${(count / analyticsData.overview.totalAppointments) * 100}%`,
                                  height: '100%',
                                  backgroundColor: '#3b82f6'
                                }} />
                              </div>
                              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1f2937', minWidth: '30px' }}>
                                {count}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ChartCard>

                    <ChartCard title="Top Clients by Revenue">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {analyticsData.clients.topClients.map((client, index) => (
                          <div key={client.userId} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0.75rem',
                            backgroundColor: index === 0 ? '#f0f9ff' : '#f9fafb',
                            borderRadius: '8px',
                            border: index === 0 ? '1px solid #bae6fd' : '1px solid #e5e7eb'
                          }}>
                            <div>
                              <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1f2937' }}>
                                {client.name}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                {client.email}
                              </div>
                            </div>
                            <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#2e7d32' }}>
                              {formatCurrency(client.revenue)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </ChartCard>

                    <ChartCard title="Performance Metrics">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <span style={{ fontSize: '0.875rem', color: '#374151' }}>Completion Rate</span>
                            <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#2e7d32' }}>
                              {formatPercentage(analyticsData.overview.completionRate)}
                            </span>
                          </div>
                          <div style={{
                            width: '100%',
                            height: '8px',
                            backgroundColor: '#e5e7eb',
                            borderRadius: '4px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${analyticsData.overview.completionRate}%`,
                              height: '100%',
                              backgroundColor: '#2e7d32'
                            }} />
                          </div>
                        </div>

                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <span style={{ fontSize: '0.875rem', color: '#374151' }}>Estimate Conversion Rate</span>
                            <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#3b82f6' }}>
                              {formatPercentage(analyticsData.estimates.conversionRate)}
                            </span>
                          </div>
                          <div style={{
                            width: '100%',
                            height: '8px',
                            backgroundColor: '#e5e7eb',
                            borderRadius: '4px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${analyticsData.estimates.conversionRate}%`,
                              height: '100%',
                              backgroundColor: '#3b82f6'
                            }} />
                          </div>
                        </div>

                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <span style={{ fontSize: '0.875rem', color: '#374151' }}>Client Retention</span>
                            <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#8b5cf6' }}>
                              {formatPercentage(analyticsData.clients.clientRetention)}
                            </span>
                          </div>
                          <div style={{
                            width: '100%',
                            height: '8px',
                            backgroundColor: '#e5e7eb',
                            borderRadius: '4px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${analyticsData.clients.clientRetention}%`,
                              height: '100%',
                              backgroundColor: '#8b5cf6'
                            }} />
                          </div>
                        </div>
                      </div>
                    </ChartCard>
                  </div>
                )}

                {/* Other tabs content would go here */}
                {activeTab !== 'OVERVIEW' && (
                  <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚧</div>
                    <h3 style={{ marginBottom: '0.5rem', color: '#374151', fontSize: '1.25rem', fontWeight: '600' }}>
                      {activeTab.charAt(0) + activeTab.slice(1).toLowerCase()} Analytics
                    </h3>
                    <p style={{ color: '#6b7280', fontSize: '1rem' }}>
                      Detailed {activeTab.toLowerCase()} analytics coming soon...
                    </p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default AdminAnalytics;
